import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Divider,
  Flex,
  InputNumber,
  Layout,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  TableColumnsType,
  Card,
  Modal,
  Spin,
} from "antd";
import { Content, Header } from "antd/es/layout/layout";
import { Link } from "react-router-dom";
import styled from "styled-components";
import dayjs from "dayjs";
import Search from "antd/es/input/Search";
import Icon from "../components/common/Icon";
import Sidebar, { TABS } from "../components/Sider";
import AcceptReferralModal from "../components/modal/AcceptReferralModal";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../redux/utils/hook";
import {
  requestAdminlogout,
  requestPasswordReset,
  requestReferralIncentiveAmount,
  requestReferrals,
  requestUpdateReferrals,
} from "../redux/admin/adminSlice";
import { isInFlightPending } from "../utils/helper";
import { InFlightNames } from "../types/types";
import { firebaseAuth } from "../config/firebase";
import {
  IncentiveType,
  ReferralAction,
  ReferralCategory,
  ReferralStatus,
  ReferrerRole,
} from "../types/admin/adminEnums";
import { ReferralData } from "../types/admin/adminTypes";

const Admin: React.FC = () => {
  const [adminNotificationApi, adminNotificationContextHolder] =
    notification.useNotification();

  const { referrals, inFlights } = useAppSelector((state) => state.admin);

  const isReferralsLoading =
    isInFlightPending(inFlights, InFlightNames.requestReferralsInFlight) ||
    isInFlightPending(inFlights, InFlightNames.requestReferredUsersInFlight);

  const isLogoutLoading = isInFlightPending(
    inFlights,
    InFlightNames.requestLogoutInFlight
  );

  const isPasswordResetLoading = isInFlightPending(
    inFlights,
    InFlightNames.requestPasswordResetInFlight
  );

  const isReferralIncentiveLoading = isInFlightPending(
    inFlights,
    InFlightNames.requestReferralIncentiveAmountInFlight
  );

  const dispatch = useAppDispatch();

  const [referralSettingsModal, setReferralSettingsModal] = useState<{
    modalState: boolean;
    referralData: any | null;
  }>({ modalState: false, referralData: null });

  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  const [selectedTab, setSelectedTab] = useState<TABS>(TABS.LEAD_MANAGEMENT);

  const [clientFkEditMode, setClientFkEditMode] = useState<{
    Id: number | null;
    clientFk: number | null;
  }>({ Id: null, clientFk: null });

  const [selectedCategory, setSelectedCategory] = useState<ReferralCategory[]>(
    []
  );
  const [selectedStatus, setSelectedStatus] = useState<ReferralStatus[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const mapReferralToDataType = (referral: any): ReferralData => ({
    key: referral.Id,
    Id: referral.Id,
    referrer: referral.user,
    referrerName: referral.user
      ? referral.user.first_name + referral.user.last_name
      : "Unknown",
    category: referral.user
      ? referral.user?.role === ReferrerRole.REFERRER
        ? ReferralCategory.EMPLOYEE
        : referral.user.role === ReferrerRole.CLIENT_ID_REFERRER
        ? ReferralCategory.TECHNICIAN
        : ReferralCategory.DISTRIBUTOR
      : ReferralCategory.EMPLOYEE,
    refereeName: referral.referred_name,
    contact: referral.referred_mobile_number,
    clientName: referral.organisation_name,
    status: referral.referral_status as ReferralStatus,
    referralAction: referral.referral_action as ReferralAction,
    createdAt: referral.CreatedAt,
    incentiveType:
      referral.incentive_type !== "NULL"
        ? (referral.incentive_type as IncentiveType)
        : null,
    incentiveStatus: referral.referral_action,
    clientFk: referral.client_fk,
    incentiveDetails: referral.incentive_details
      ? JSON.parse(referral.incentive_details)
      : [],
    incentiveEndDate: referral.completed_at
      ? dayjs(referral.completed_at)
      : null,
    referralOnboardedDate: referral.onboarded_at
      ? dayjs(referral.onboarded_at)
      : null,
    modality: referral.modality?.split(",") ?? [],
    commission:
      referral.incentive_type === IncentiveType.COMMISSION
        ? JSON.parse(referral?.incentive_details)?.[0]?.["commission"] ?? 0
        : 0,
    incentiveTypePerCase:
      referral.incentive_type === IncentiveType.PER_CASE
        ? JSON.parse(referral.incentive_details)
        : [],
    yaakeRef: referral.yaake_ref,
    incentiveData: referral.incentiveData,
  });

  const leadManagementData = useMemo(() => {
    if (referrals.length) {
      return referrals.map(mapReferralToDataType).filter((referral) => {
        const categoryMatch =
          selectedCategory.length === 0 ||
          selectedCategory.includes(referral.category);

        const statusMatch =
          selectedStatus.length === 0 ||
          selectedStatus.includes(referral.status);

        const searchMatch =
          !searchTerm ||
          referral.referrerName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          referral.refereeName.toLowerCase().includes(searchTerm.toLowerCase());

        return categoryMatch && statusMatch && searchMatch;
      });
    }
    return [];
  }, [referrals, selectedCategory, selectedStatus, searchTerm]);

  const incentiveData = useMemo(() => {
    return leadManagementData.filter((data) =>
      [ReferralAction.ACCEPTED, ReferralAction.ENDED].includes(
        data.referralAction
      )
    );
  }, [leadManagementData]);

  useEffect(() => {
    if (!referrals.length) {
      dispatch(requestReferrals());
    }
  }, []);

  const handleOnMenuSelect = (tab: TABS) => {
    setSelectedTab(tab);
  };

  const handleLogout = async () => {
    await dispatch(requestAdminlogout());
  };

  const handleRejectIncentive = async (record: ReferralData) => {
    await dispatch(
      requestUpdateReferrals({
        Id: record.Id,
        referralAction: ReferralAction.REJECTED,
        adminNotificationApi,
        updateFor: "the referral status to Rejected",
      })
    );
  };

  const handleEndIncentive = async (record: ReferralData) => {
    await dispatch(
      requestUpdateReferrals({
        Id: record.Id,
        referralAction: ReferralAction.ENDED,
        adminNotificationApi,
        updateFor: "Referral Details",
      })
    );
  };

  const handleReferralModalUpdate = async (values: any) => {
    await dispatch(
      requestUpdateReferrals({
        ...values,
        adminNotificationApi,
        updateFor: "Referral Details",
      })
    );
  };

  const handleUpdateClientId = async () => {
    await dispatch(
      requestUpdateReferrals({
        Id: clientFkEditMode.Id!,
        clientFk: clientFkEditMode.clientFk!,
        adminNotificationApi,
        updateFor: "Client Id",
      })
    );
    setClientFkEditMode({ Id: null, clientFk: null });
  };

  const handleResetPassword = async () => {
    await dispatch(
      requestPasswordReset({
        email: firebaseAuth.currentUser?.email!,
        adminNotificationApi,
      })
    );
  };

  const handleGetIncentiveDetails = async (
    expanded: boolean,
    record: ReferralData
  ) => {
    if (expanded && !record.incentiveData) {
      await dispatch(
        requestReferralIncentiveAmount({ user_fk: record.referrer.Id })
      );
    }
  };

  const statusOptions = Object.values(ReferralStatus).map((status) => ({
    value: status,
    label: status,
  }));

  const categoryOptions = Object.values(ReferralCategory).map((category) => ({
    value: category,
    label: category,
  }));

  return (
    <Layout style={{ height: "100vh", backgroundColor: "white" }}>
      {adminNotificationContextHolder}

      <StyledHeader>
        <EllipseTopRight />
        <Row style={{ margin: "1.1rem 0" }}>
          <Icon name="logo" width={70} />
        </Row>
        <Divider style={{ backgroundColor: "grey", margin: 0 }} />
        <AdminTitleContainer align="middle">
          <Typography.Title style={{ color: "white" }}>
            Hello Admin{" "}
          </Typography.Title>
          <Icon name="admin" width={35} />
        </AdminTitleContainer>
      </StyledHeader>

      <Layout style={{ height: "100%" }}>
        <Sidebar
          selectedTab={selectedTab}
          onMenuSelect={handleOnMenuSelect}
          handleLogout={handleLogout}
          isLogoutLoading={isLogoutLoading}
        />

        <Content style={{ padding: "2rem" }}>
          {selectedTab !== TABS.MY_PROFILE && (
            <Flex justify="flex-end" gap={15} style={{ margin: "0" }}>
              <Flex vertical>
                <Typography.Text>Category</Typography.Text>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select a Category"
                  optionFilterProp="label"
                  onChange={(value) => setSelectedCategory(value)}
                  options={categoryOptions}
                  style={{ width: 230 }}
                  maxTagCount="responsive"
                />
              </Flex>
              <Flex vertical>
                <Typography.Text>Status</Typography.Text>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select a Status"
                  optionFilterProp="label"
                  onChange={(value) => setSelectedStatus(value)}
                  options={statusOptions}
                  style={{ width: 230 }}
                  maxTagCount="responsive"
                />
              </Flex>
              <Flex vertical>
                <Typography.Text>Search</Typography.Text>
                <Search
                  placeholder="Search referrals"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 230 }}
                />
              </Flex>
            </Flex>
          )}

          {selectedTab === TABS.LEAD_MANAGEMENT && (
            <Table<ReferralData>
              loading={isReferralsLoading}
              bordered={false}
              scroll={{ x: 3, y: 320 }}
              style={{ width: "100%", marginTop: "1rem" }}
              columns={getLeadManagementColumns(
                handleRejectIncentive,
                setReferralSettingsModal
              )}
              dataSource={leadManagementData}
              expandable={{
                expandedRowRender: (record) => (
                  <ReferralDetailsExpand
                    record={record}
                    clientFkEditMode={clientFkEditMode}
                    setClientFkEditMode={setClientFkEditMode}
                    handleUpdateClientId={handleUpdateClientId}
                  />
                ),
              }}
            />
          )}

          {selectedTab === TABS.INCENTIVE_MANAGEMENT && (
            <Table<ReferralData>
              bordered={false}
              scroll={{ x: 3, y: 320 }}
              style={{ width: "100%", marginTop: "1rem" }}
              columns={getIncentiveColumns(handleEndIncentive)}
              dataSource={incentiveData}
              expandable={{
                onExpand: handleGetIncentiveDetails,
                expandedRowRender: (record) => (
                  <IncentiveDetailsExpand
                    record={record}
                    isLoading={isReferralIncentiveLoading}
                  />
                ),
              }}
            />
          )}

          {selectedTab === TABS.MY_PROFILE && (
            <ProfileContainer>
              <StyledCard bordered={false}>
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <InfoSection>
                    <InfoHeader type="secondary">
                      <MailOutlined style={{ marginRight: 8 }} />
                      EMAIL ADDRESS
                    </InfoHeader>
                    <InfoContent>
                      <Typography.Text strong>
                        {firebaseAuth.currentUser?.email}
                      </Typography.Text>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        disabled
                        title="Email cannot be changed"
                      />
                    </InfoContent>
                  </InfoSection>

                  <Divider style={{ margin: "12px 0" }} />

                  <InfoSection>
                    <InfoHeader type="secondary">
                      <LockOutlined style={{ marginRight: 8 }} />
                      PASSWORD
                    </InfoHeader>
                    <InfoContent>
                      <Typography.Text strong>••••••••</Typography.Text>
                      <Button
                        type="primary"
                        onClick={() => setShowPasswordResetModal(true)}
                      >
                        Reset Password
                      </Button>
                    </InfoContent>
                  </InfoSection>
                </Space>
              </StyledCard>

              <Modal
                title="Reset Password"
                open={showPasswordResetModal}
                onCancel={() => setShowPasswordResetModal(false)}
                footer={null}
              >
                <p>
                  We'll send a password reset link to your email address:{" "}
                  {firebaseAuth.currentUser?.email}
                </p>
                <ModalFooter>
                  <Space>
                    <Button onClick={() => setShowPasswordResetModal(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleResetPassword}
                      loading={isPasswordResetLoading}
                    >
                      Send Reset Link
                    </Button>
                  </Space>
                </ModalFooter>
              </Modal>
            </ProfileContainer>
          )}
        </Content>

        <AcceptReferralModal
          openModal={referralSettingsModal.modalState}
          referralData={referralSettingsModal.referralData}
          onModalUpdate={handleReferralModalUpdate}
          onCloseModal={() =>
            setReferralSettingsModal({ referralData: null, modalState: false })
          }
        />
      </Layout>
    </Layout>
  );
};

const getLeadManagementColumns = (
  handleRejectIncentive: (record: ReferralData) => void,
  setReferralSettingsModal: React.Dispatch<
    React.SetStateAction<{
      modalState: boolean;
      referralData: any | null;
    }>
  >
): TableColumnsType<ReferralData> => [
  { title: "Referrer Name", dataIndex: "referrerName" },
  { title: "Category", dataIndex: "category" },
  { title: "Referee Name", dataIndex: "refereeName" },
  { title: "Contact", dataIndex: "contact" },
  { title: "Client Name", dataIndex: "clientName" },
  { title: "Status", dataIndex: "status" },
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <Space size="middle">
        {record.referralAction === ReferralAction.PENDING ? (
          <>
            <AcceptIcon
              onClick={() =>
                setReferralSettingsModal({
                  referralData: record,
                  modalState: true,
                })
              }
            />
            <Popconfirm
              title="Sure to Reject the Referral?"
              onConfirm={() => handleRejectIncentive(record)}
            >
              <RejectIcon />
            </Popconfirm>
          </>
        ) : (
          <>
            <Typography.Text>
              <Tag
                icon={
                  record.referralAction === ReferralAction.ACCEPTED ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
                color={
                  record.referralAction === ReferralAction.ACCEPTED
                    ? "green"
                    : "red"
                }
              >
                {record.referralAction}
              </Tag>
            </Typography.Text>
            {record.referralAction === ReferralAction.ACCEPTED && (
              <EditOutlined
                onClick={() =>
                  setReferralSettingsModal({
                    referralData: record,
                    modalState: true,
                  })
                }
              />
            )}
          </>
        )}
      </Space>
    ),
  },
  Table.EXPAND_COLUMN,
];

const getIncentiveColumns = (
  handleEndIncentive: (record: ReferralData) => void
): TableColumnsType<ReferralData> => [
  { title: "Referrer Name", dataIndex: "referrerName" },
  { title: "Incentive Type", dataIndex: "incentiveType" },
  { title: "Client Name", dataIndex: "clientName" },
  {
    title: "Incentive End Date",
    dataIndex: "incentiveEndDate",
    render: (_, record) => (
      <Typography.Text>
        {dayjs(record.incentiveEndDate).format("DD MMM YYYY")}
      </Typography.Text>
    ),
  },
  { title: "Status", dataIndex: "status" },
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <Flex justify="center">
        {record.referralAction !== ReferralAction.ENDED ? (
          <Popconfirm
            title="Are you sure want to End Incentive?"
            onConfirm={() => handleEndIncentive(record)}
          >
            <Button
              type="text"
              danger
              size="small"
              style={{ fontWeight: "bold" }}
            >
              End Incentive
            </Button>
          </Popconfirm>
        ) : (
          <Tag icon={<MinusCircleOutlined />} color="volcano">
            Ended
          </Tag>
        )}
      </Flex>
    ),
  },
  Table.EXPAND_COLUMN,
];

const ReferralDetailsExpand: React.FC<{
  record: ReferralData;
  clientFkEditMode: { Id: number | null; clientFk: number | null };
  setClientFkEditMode: React.Dispatch<
    React.SetStateAction<{
      Id: number | null;
      clientFk: number | null;
    }>
  >;
  handleUpdateClientId: () => Promise<void>;
}> = ({
  record,
  clientFkEditMode,
  setClientFkEditMode,
  handleUpdateClientId,
}) => (
  <Flex
    style={{
      margin: 0,
      backgroundColor: "#EBF4FF",
      borderRadius: "0.7rem",
      padding: "1rem 2rem",
    }}
    vertical
    justify="center"
    key={record.Id}
  >
    <Typography.Title level={5} style={{ marginTop: 0 }}>
      Referral Information
    </Typography.Title>

    <Row gutter={[16, 16]}>
      <Col span={6}>
        <Typography.Text>
          <strong>Referral Status:</strong> {record.status}
        </Typography.Text>
      </Col>
      <Col span={6}>
        <Typography.Text>
          <strong>Created Date:</strong>{" "}
          {dayjs(record.createdAt).format("DD MMM YYYY")}
        </Typography.Text>
      </Col>

      <Col span={6}>
        <Typography.Text>
          <strong>Yaake Ref:</strong>{" "}
          {record.yaakeRef ? (
            <Link target="_blank" to={record.yaakeRef}>
              Link
            </Link>
          ) : (
            <Typography.Text>NIL</Typography.Text>
          )}
        </Typography.Text>
      </Col>
      <Col span={6}>
        <Flex align="center" gap={4}>
          <strong>Client Id:</strong>
          {clientFkEditMode.Id === record.Id ? (
            <>
              <InputNumber
                value={clientFkEditMode.clientFk}
                onChange={(value) =>
                  setClientFkEditMode({
                    ...clientFkEditMode,
                    clientFk: value,
                  })
                }
                style={{ width: "5rem" }}
              />
              <CheckCircleOutlined onClick={handleUpdateClientId} />
              <CloseCircleOutlined
                onClick={() =>
                  setClientFkEditMode({
                    Id: null,
                    clientFk: null,
                  })
                }
              />
            </>
          ) : (
            <>
              <Typography.Text
                style={{
                  backgroundColor: "white",
                  padding: "0.2rem 0.5rem",
                  borderRadius: "0.5rem",
                  width: "5rem",
                }}
              >
                {record.clientFk ?? "-- -- --"}
              </Typography.Text>
              <EditOutlined
                onClick={() =>
                  setClientFkEditMode({
                    Id: record.Id,
                    clientFk: record.clientFk,
                  })
                }
              />
            </>
          )}
        </Flex>
      </Col>
    </Row>

    {record.referralAction === "Accepted" && (
      <>
        <Typography.Title level={5}>Incentive Information</Typography.Title>

        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Typography.Text>
              <strong>Incentive Type:</strong> {record.incentiveType}
            </Typography.Text>
          </Col>
          <Col>
            <Typography.Text>
              <strong>Incentive Status:</strong> {record.incentiveStatus}
            </Typography.Text>
          </Col>
        </Row>
      </>
    )}
  </Flex>
);

const IncentiveDetailsExpand: React.FC<{
  record: ReferralData;
  isLoading: boolean;
}> = ({ record, isLoading }) => (
  <Flex
    style={{
      margin: 0,
      backgroundColor: "#EBF4FF",
      borderRadius: "0.7rem",
      padding: "1rem 2rem",
    }}
    vertical
  >
    <Typography.Title level={5} style={{ marginTop: 0 }}>
      Referral Information
    </Typography.Title>

    <Row gutter={[16, 8]}>
      <Col span={7}>
        <Typography.Text>
          <strong>Referral Status:</strong> Onboarded on{" "}
          {dayjs(record.referralOnboardedDate).format("DD MMM YYYY")}
        </Typography.Text>
      </Col>
      <Col span={5}>
        <Typography.Text>
          <strong>Yaake Ref:</strong>
          {record.yaakeRef ? (
            <Link to={record.yaakeRef} target="_blank">
              Link
            </Link>
          ) : (
            "NIL"
          )}
        </Typography.Text>
      </Col>

      <Col span={6}>
        <Typography.Text>
          <strong>Modality:</strong> {record.modality?.join(", ")}
        </Typography.Text>
      </Col>
      <Col span={6}>
        <Flex align="center" gap={4}>
          <strong>Client Id:</strong> {record.clientFk}
        </Flex>
      </Col>
    </Row>

    <Typography.Title level={5}>Incentive Information</Typography.Title>

    <Row gutter={[16, 8]} align="middle">
      {isLoading ? (
        <Spin />
      ) : (
        <Col span={12}>
          {record.incentiveData ? (
            <Flex align="center" gap={5}>
              <Typography.Text>
                <strong>Incentive Offered:</strong>
              </Typography.Text>
              <strong>{record.incentiveData?.incentive}</strong>
              <Icon name="incentive_coin" width={15} hideMargin />
            </Flex>
          ) : (
            <Typography.Text>No Data</Typography.Text>
          )}
        </Col>
      )}
    </Row>
  </Flex>
);

const StyledHeader = styled(Header)`
  background: linear-gradient(to right, #171c2a, #0d121f);
  margin: 0;
  padding: 0;
  color: #fff;
  height: 30%;
`;

const AdminTitleContainer = styled(Row)`
  padding: 0 3rem;
  height: 69%;
`;

const EllipseTopRight = styled.div`
  position: fixed;
  top: -400px;
  right: -400px;
  width: 850px;
  height: 850px;
  border-radius: 50%;
  border: 100px solid rgba(255, 255, 255, 0.1);
  opacity: 80%;
  @media (max-width: 768px) {
    width: 500px;
    height: 500px;
    top: -250px;
    right: -250px;
  }
`;

const AcceptIcon = styled(CheckCircleOutlined)`
  font-size: 1.3rem;
  color: green;
  cursor: pointer;
  display: inline-block;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.2);
  }
`;

const RejectIcon = styled(CloseCircleOutlined)`
  font-size: 1.3rem;
  color: red;
  cursor: pointer;
  display: inline-block;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.2);
  }
`;

const ProfileContainer = styled.section`
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
`;

const StyledCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
`;

const InfoSection = styled.div`
  margin-bottom: 8px;
`;

const InfoHeader = styled(Typography.Text)`
  display: flex;
  align-items: center;
`;

const InfoContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const ModalFooter = styled.div`
  margin-top: 24px;
  text-align: right;
`;

export default Admin;
